import type {
  Estimate,
  EstimateSource,
  ItemState,
  MealItem,
  MealType,
  NutritionTotals,
} from '../api/types';

/**
 * El umbral de RN-06: por debajo de esto el ítem pide confirmación y el número se muestra como
 * banda. El servidor ya decide el estado y ya calcula la banda; acá sólo se lee lo que mandó.
 */
export const LOW_CONFIDENCE_THRESHOLD = 0.6;

const integer = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatKcal(kcal: number): string {
  return integer.format(Math.round(kcal));
}

export function formatGrams(grams: number): string {
  return oneDecimal.format(grams);
}

export function formatPercentage(fraction: number): string {
  return `${integer.format(Math.round(fraction))} %`;
}

/**
 * Un número incierto se ve incierto (principio 2 del sistema de diseño). Cuando el servidor
 * mandó banda, la banda ES el valor: mostrar el punto medio con una nota al pie sería decir
 * "son 480" y desmentirlo en letra chica.
 */
export function formatEstimateKcal(estimate: Estimate): string {
  if (estimate.kcalLow !== null && estimate.kcalHigh !== null) {
    return `${formatKcal(estimate.kcalLow)}–${formatKcal(estimate.kcalHigh)}`;
  }

  return formatKcal(estimate.kcal);
}

export function isUncertain(estimate: Estimate): boolean {
  return estimate.kcalLow !== null && estimate.kcalHigh !== null;
}

/**
 * Lo mismo para un agregado. El servidor propaga la banda al día y a cada comida, así que acá no
 * se decide nada: si los extremos difieren, es un rango.
 */
export function formatTotalKcal(totals: NutritionTotals): string {
  return totals.kcalLow === totals.kcalHigh
    ? formatKcal(totals.kcal)
    : `${formatKcal(totals.kcalLow)}–${formatKcal(totals.kcalHigh)}`;
}

export function isRange(totals: NutritionTotals): boolean {
  return totals.kcalLow !== totals.kcalHigh;
}

/**
 * Un cociente se escribe con dos decimales.
 *
 * `formatGrams` redondea a uno, que para gramos está bien y para un índice cintura-altura no:
 * 0,45 y 0,48 son lecturas distintas y las dos se verían como «0,5».
 */
export function formatRatio(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * El nombre de la fórmula, en castellano.
 *
 * RF-38 pide que las métricas derivadas se muestren con la fórmula que las produjo. El servidor
 * la manda como identificador (`navy_body_fat`), que es lo correcto para un contrato pero no para
 * leer: nadie escribe «navy_body_fat» cuando habla de su porcentaje de grasa. El identificador
 * desconocido se muestra tal cual antes que ocultarlo — la trazabilidad importa más que la
 * prolijidad.
 */
const FORMULA_LABELS: Record<string, string> = {
  body_mass_index: 'peso / altura²',
  waist_to_height_ratio: 'cintura / altura',
  navy_body_fat: 'método Navy',
  fat_free_mass: 'peso − masa grasa',
};

export function formulaLabel(formula: string, version: string): string {
  return `${FORMULA_LABELS[formula] ?? formula} · v${version}`;
}

/** La confianza como la escribe el castellano rioplatense: `0,50`. */
export function formatConfidence(confidence: number): string {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(confidence);
}

/**
 * De dónde salió el número. La palabra importa tanto como la barra: el indicador dice *cuánta*
 * certeza hay, y esto dice *por qué*.
 */
const SOURCE_LABELS: Record<EstimateSource, string> = {
  Barcode: 'EAN',
  UserCorrection: 'editado por vos',
  NutritionLabel: 'rótulo',
  Catalog: 'catálogo',

  // RN-22 · «foto» describía el insumo y ocultaba quién sacó el número. Que la palabra aparezca
  // en todos lados donde se muestra el origen es la mitad de la regla; la otra mitad es la
  // advertencia de cómo obtener un valor preciso, y esa vive en el detalle del ítem.
  Vision: 'analizada por IA',
  Declaration: 'analizada por IA',
};

export function sourceLabel(source: EstimateSource): string {
  return SOURCE_LABELS[source];
}

/**
 * Si el número lo produjo un modelo (RN-22).
 *
 * Existe como función y no como comparación suelta porque la regla es «toda estimación asistida por
 * IA se declara», y esa lista creció: era sólo `Vision` hasta que RN-24 agregó el agente que lee la
 * declaración escrita. Con la comparación repetida en tres pantallas, agregar la segunda fuente
 * habría dejado alguna sin la advertencia — y la que queda sin advertencia es exactamente la que
 * alguien va a creer.
 */
export function isAiEstimate(source: EstimateSource): boolean {
  return source === 'Vision' || source === 'Declaration';
}

/**
 * Cómo se llama un ítem en una lista.
 *
 * El texto libre primero porque es lo que la persona dijo (RN-01), y recién después la descripción
 * que generó el análisis. Una foto sin descripción escrita es el caso normal del agente: sin este
 * respaldo la fila diría «Ítem» y un rango de calorías sin sujeto.
 */
export function itemTitle(item: MealItem, fallback = 'Ítem'): string {
  return item.freeText ?? item.currentEstimate?.detail ?? item.ean ?? fallback;
}

/** El texto de RN-22, en un solo lugar para que no se reescriba distinto en cada pantalla. */
export const AI_ESTIMATE_ADVICE =
  'Para que sea preciso, pesá el alimento y, si tiene rótulo o código de barras, incluilo.';

/**
 * Los pasos del indicador de confianza · variante A, cuatro barras.
 *
 * Deuda conocida del rediseño: rótulo (0,90) y catálogo (0,70) comparten tres barras y sólo se
 * distinguen por la palabra de origen. Pasar a cinco barras daría un paso único a cada fuente.
 */
export function confidenceSteps(confidence: number): 1 | 2 | 3 | 4 {
  if (confidence >= 0.95) {
    return 4;
  }

  if (confidence >= 0.7) {
    return 3;
  }

  if (confidence >= 0.4) {
    return 2;
  }

  return 1;
}

const MEAL_TYPE_LABELS: Record<MealType, string> = {
  Breakfast: 'Desayuno',
  Lunch: 'Almuerzo',
  AfternoonSnack: 'Merienda',
  Dinner: 'Cena',
  Snack: 'Colación',
};

export function mealTypeLabel(type: MealType): string {
  return MEAL_TYPE_LABELS[type];
}

export const MEAL_TYPES: MealType[] = ['Breakfast', 'Lunch', 'AfternoonSnack', 'Dinner', 'Snack'];

/**
 * Los textos de estado. Ninguno culpa a la persona ni al sistema: describen qué falta y quién
 * puede hacerlo, que es lo único accionable.
 */
const ITEM_STATE_LABELS: Record<ItemState, string> = {
  Pending: 'Estimando',
  Resolved: 'Estimado',
  NeedsConfirmation: 'Revisá esto',
  Confirmed: 'Confirmado',
  Unresolved: 'Sin estimar',
  Failed: 'No se pudo estimar',
};

export function itemStateLabel(state: ItemState): string {
  return ITEM_STATE_LABELS[state];
}

export type StateTone = 'neutral' | 'attention' | 'done' | 'muted';

export function itemStateTone(state: ItemState): StateTone {
  switch (state) {
    case 'Confirmed':
    case 'Resolved':
      return 'done';
    case 'NeedsConfirmation':
      return 'attention';
    case 'Pending':
      return 'neutral';
    case 'Unresolved':
    case 'Failed':
      return 'muted';
  }
}

const UNIT_LABELS = {
  Gram: 'g',
  Milliliter: 'ml',
  Unit: 'u',
  Serving: 'porción',
} as const;

export function unitLabel(unit: keyof typeof UNIT_LABELS): string {
  return UNIT_LABELS[unit];
}
